import { useEffect, useRef, useState } from "react";
import type { ApiClient, ReminderResponse } from "../../shared/api/client";
import { useLocale, useT } from "../../shared/i18n";
import {
  Banner,
  Button,
  Card,
  DateTimePicker,
  Fab,
  Field,
  GroupLabel,
  Icons,
  Input,
  ListRow,
  Segmented,
  Select,
  Sheet,
  Skeleton,
} from "../../shared/ui";
import { ReminderDetail } from "./ReminderDetail";
import { reminderStatus } from "./status";
import { clockLabel, dayLabel, localNow } from "./time";

export function Reminders({ api }: { api: ApiClient }) {
  const t = useT();
  const { locale } = useLocale();
  const [items, setItems] = useState<ReminderResponse[] | null>(null);
  const [error, setError] = useState(false);
  const [filter, setFilter] = useState<"today" | "week" | "all">("today");
  const [editing, setEditing] = useState<ReminderResponse | "new" | null>(null);
  const [title, setTitle] = useState("");
  const [schedule, setSchedule] = useState("");
  const [frequency, setFrequency] =
    useState<ReminderResponse["recurrence"]>("once");
  const [busy, setBusy] = useState(false);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const [detailFailed, setDetailFailed] = useState(false);
  const [reload, setReload] = useState(0);
  const key = useRef<{ body: string; key: string } | null>(null);
  useEffect(() => {
    let active = true;
    void api
      .reminders()
      .then((value) => {
        if (active) {
          setItems(value);
          setError(false);
        }
      })
      .catch(() => {
        if (active) setError(true);
      });
    return () => {
      active = false;
    };
  }, [api, reload]);
  function open(item: ReminderResponse | "new") {
    setEditing(item);
    setTitle(item === "new" ? "" : item.title);
    setSchedule(item === "new" ? "" : item.local_time.slice(0, 16));
    setFrequency(item === "new" ? "once" : item.recurrence);
    key.current = null;
    setError(false);
  }
  async function save() {
    if (!editing || !title.trim() || !schedule) return;
    setBusy(true);
    setError(false);
    try {
      const values = {
        title: title.trim(),
        local_schedule: schedule,
        recurrence: frequency,
      };
      if (editing === "new") {
        const body = JSON.stringify(values);
        if (key.current?.body !== body)
          key.current = { body, key: crypto.randomUUID() };
        await api.createReminder({
          ...values,
          timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
          idempotency_key: key.current.key,
        });
      } else
        await api.updateReminder(editing.reminder_id, {
          ...values,
          interval: editing.recurrence === frequency ? editing.interval : 1,
          revision: editing.revision,
        });
      setEditing(null);
      setReload((value) => value + 1);
    } catch {
      setError(true);
    } finally {
      setBusy(false);
    }
  }
  async function change(
    run: (item: ReminderResponse) => Promise<unknown>,
  ): Promise<boolean> {
    const item = items?.find(
      (candidate) => candidate.reminder_id === selectedId,
    );
    if (!item) return false;
    setBusy(true);
    setDetailFailed(false);
    try {
      const result = await run(item);
      if (typeof result === "object" && result !== null && "deleted" in result)
        setSelectedId(null);
      setReload((value) => value + 1);
      return true;
    } catch {
      setDetailFailed(true);
      return false;
    } finally {
      setBusy(false);
    }
  }
  const selected = items?.find((item) => item.reminder_id === selectedId);
  const today = new Date();
  today.setHours(0, 0, 0, 0);
  const end = new Date(today);
  end.setDate(end.getDate() + (filter === "week" ? 7 : 1));
  const visible = (items ?? []).filter(
    (item) =>
      filter === "all" ||
      (item.state === "active" && new Date(item.next_occurrence_at) < end),
  );
  const periods = (["today", "week", "all"] as const).map((value) => ({
    id: value,
    label: t(`reminders.${value}`),
  }));
  return (
    <section className="mx-auto w-full max-w-3xl px-gutter pb-28">
      <Segmented
        label={t("reminders.filters")}
        options={periods}
        value={filter}
        onChange={(value) => {
          if (value === "today" || value === "week" || value === "all")
            setFilter(value);
        }}
      />
      {error && editing === null && (
        <div className="mt-4">
          <Banner tone="error">
            {t("record.error")}{" "}
            <Button
              variant="tinted"
              size="xs"
              onClick={() => {
                setReload((value) => value + 1);
              }}
            >
              {t("error.retry")}
            </Button>
          </Banner>
        </div>
      )}
      {items === null && !error ? (
        <div className="mt-4 flex flex-col gap-3">
          <Skeleton height={64} />
          <Skeleton height={64} />
        </div>
      ) : (
        <>
          <GroupLabel className="px-1 pb-2 pt-5">
            {t(`reminders.${filter}`)}
          </GroupLabel>
          {visible.length === 0 ? (
            <p className="p-6 text-center text-body text-fg-muted">
              {t("reminders.empty")}
            </p>
          ) : (
            <Card flush className="overflow-hidden">
              {visible.map((item) => {
                const status = reminderStatus(item);
                const time = clockLabel(item.local_time);
                const subtitle =
                  status === "stopped"
                    ? t("reminders.stopped.title")
                    : status === "sent"
                      ? t("reminders.sent")
                      : [
                          dayLabel(
                            locale,
                            item.local_time,
                            localNow(item.timezone),
                          ),
                          time,
                          ...(item.recurrence === "once"
                            ? []
                            : [
                                t(`reminders.${item.recurrence}`).toLowerCase(),
                              ]),
                          ...(status === "missed"
                            ? [t("reminders.overdue")]
                            : []),
                        ].join(" · ");
                return (
                  <ListRow
                    key={item.reminder_id}
                    kind="reminder"
                    title={item.title}
                    subtitle={subtitle}
                    marker={
                      status === "missed"
                        ? "warning"
                        : status === "active"
                          ? "system"
                          : "none"
                    }
                    trailing={
                      status === "active" || status === "missed"
                        ? time
                        : undefined
                    }
                    onClick={() => {
                      setDetailFailed(false);
                      setSelectedId(item.reminder_id);
                    }}
                  />
                );
              })}
            </Card>
          )}
        </>
      )}
      <Fab
        label={t("reminders.add")}
        text={t("reminders.add")}
        aboveActionBar
        onClick={() => {
          open("new");
        }}
      >
        <Icons.Plus size={20} />
      </Fab>
      <Sheet
        open={editing !== null}
        onOpenChange={(value) => {
          if (!value && !busy) setEditing(null);
        }}
        title={t(editing === "new" ? "reminders.add" : "record.edit")}
        description={
          editing && editing !== "new" ? editing.timezone : undefined
        }
      >
        <form
          className="flex flex-col gap-4"
          onSubmit={(event) => {
            event.preventDefault();
            void save();
          }}
        >
          <Field
            label={t("reminders.title")}
            required
            requiredLabel={t("record.required")}
          >
            {(props) => (
              <Input
                {...props}
                required
                maxLength={160}
                value={title}
                onChange={(event) => {
                  setTitle(event.target.value);
                }}
              />
            )}
          </Field>
          <Field label={t("reminders.time")}>
            {(props) => (
              <DateTimePicker
                {...props}
                value={schedule}
                onValueChange={setSchedule}
              />
            )}
          </Field>
          <Field label={t("reminders.repeat")}>
            {(props) => (
              <Select
                {...props}
                value={frequency}
                onValueChange={(value) => {
                  if (
                    value === "once" ||
                    value === "daily" ||
                    value === "weekly" ||
                    value === "monthly"
                  )
                    setFrequency(value);
                }}
                options={(["once", "daily", "weekly", "monthly"] as const).map(
                  (value) => ({ value, label: t(`reminders.${value}`) }),
                )}
              />
            )}
          </Field>
          {error && (
            <p role="alert" className="text-footnote text-error">
              {t("record.error")}
            </p>
          )}
          <Button
            type="submit"
            className="w-full"
            disabled={busy || !title.trim() || !schedule}
            loading={busy}
          >
            {t("record.save")}
          </Button>
        </form>
      </Sheet>
      {selected && (
        <ReminderDetail
          key={selected.reminder_id}
          reminder={selected}
          busy={busy}
          failed={detailFailed}
          onBack={() => {
            setSelectedId(null);
          }}
          onEdit={() => {
            open(selected);
          }}
          onReschedule={(localSchedule) =>
            change((item) =>
              api.updateReminder(item.reminder_id, {
                revision: item.revision,
                local_schedule: localSchedule,
              }),
            )
          }
          onTransition={async (operation) => {
            await change((item) =>
              api.transitionReminder(
                item.reminder_id,
                item.revision,
                operation,
              ),
            );
          }}
        />
      )}
    </section>
  );
}
